import React, { useState, useCallback, useMemo } from 'react';
import { Building2, Loader2 } from 'lucide-react';
import {
  tenantRegistrationService,
  buildTenantUrl,
  extractApiError,
} from '../../services/tenantRegistrationService';
import type { RegisterTenantRequest } from '../../types/tenantRegistration';

// Same rules enforced by the API (RegisterTenantModel + TenantManager.CustomTenancyNameRegex)
const TENANCY_NAME_REGEX = /^[a-zA-Z0-9]+$/;
const TENANCY_NAME_MAX_LENGTH = 20;
const NAME_MAX_LENGTH = 32;
const EMAIL_MAX_LENGTH = 256;

function isValidEmail(email: string): boolean
{
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

interface FormState {
  tenancyName: string;
  firstName: string;
  lastName: string;
  emailAddress: string;
}

const EMPTY_FORM: FormState = {
  tenancyName: '',
  firstName: '',
  lastName: '',
  emailAddress: '',
};

interface RegisterTenantFormProps {
  onRegistered: (request: RegisterTenantRequest, tenantUrl: string) => void;
}

export const RegisterTenantForm: React.FC<RegisterTenantFormProps> = ({ onRegistered }) =>
{
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [touched, setTouched] = useState<Record<keyof FormState, boolean>>({
    tenancyName: false,
    firstName: false,
    lastName: false,
    emailAddress: false,
  });
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState('');
  const [result, setResult] = useState<{ tenancyName: string; url: string; email: string } | null>(
    null,
  );

  const errors = useMemo(() =>
  {
    const next: Partial<Record<keyof FormState, string>> = {};
    const tenancy = form.tenancyName.trim();
    if (!tenancy) next.tenancyName = 'Tenancy name is required.';
    else if (tenancy.length > TENANCY_NAME_MAX_LENGTH)
    {
      next.tenancyName = `Maximum ${TENANCY_NAME_MAX_LENGTH} characters.`;
    }
    else if (!TENANCY_NAME_REGEX.test(tenancy))
    {
      next.tenancyName = 'Only letters and numbers, no spaces or special characters.';
    }

    if (!form.firstName.trim()) next.firstName = 'First name is required.';
    else if (form.firstName.trim().length > NAME_MAX_LENGTH)
    {
      next.firstName = `Maximum ${NAME_MAX_LENGTH} characters.`;
    }

    if (!form.lastName.trim()) next.lastName = 'Last name is required.';
    else if (form.lastName.trim().length > NAME_MAX_LENGTH)
    {
      next.lastName = `Maximum ${NAME_MAX_LENGTH} characters.`;
    }

    const email = form.emailAddress.trim();
    if (!email) next.emailAddress = 'Administrator email is required.';
    else if (!isValidEmail(email) || email.length > EMAIL_MAX_LENGTH)
    {
      next.emailAddress = 'A valid e-mail is required.';
    }
    return next;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) =>
  {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setApiError('');
  }, []);

  const handleBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) =>
  {
    const name = e.target.name as keyof FormState;
    setTouched(prev => ({ ...prev, [name]: true }));
  }, []);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) =>
    {
      e.preventDefault();
      setTouched({ tenancyName: true, firstName: true, lastName: true, emailAddress: true });
      if (!isValid || loading) return;

      const request: RegisterTenantRequest = {
        TenancyName: form.tenancyName.trim(),
        FirstName: form.firstName.trim(),
        LastName: form.lastName.trim(),
        EmailAddress: form.emailAddress.trim(),
        LoginProvider: null,
        ProviderKey: null,
        IsExternalLogin: false,
      };

      setLoading(true);
      setApiError('');
      setResult(null);

      try
      {
        const tenantUrl = await tenantRegistrationService.registerTenant(request);
        setResult({ tenancyName: request.TenancyName, url: tenantUrl, email: request.EmailAddress });
        setForm(EMPTY_FORM);
        setTouched({ tenancyName: false, firstName: false, lastName: false, emailAddress: false });
        onRegistered(request, tenantUrl);
      }
      catch (error)
      {
        setApiError(extractApiError(error, 'The tenant could not be created.'));
      }
      finally
      {
        setLoading(false);
      }
    },
    [form, isValid, loading, onRegistered],
  );

  const previewUrl = form.tenancyName.trim() ? buildTenantUrl(form.tenancyName) : '';

  const fieldClass = (name: keyof FormState) =>
    `w-full px-3 py-2 border rounded-md text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#62accb] focus:border-transparent disabled:bg-gray-100 ${
      touched[name] && errors[name] ? 'border-red-400' : 'border-gray-300'
    }`;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Building2 className="w-5 h-5 text-[#62accb]" />
        <h2 className="text-lg font-medium text-gray-800">Create new tenant</h2>
      </div>
      <p className="text-sm text-gray-500 mb-5">
        Creates the tenant database, its administrator account and sends the welcome email with
        the access credentials.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">Tenancy name</label>
          <input
            type="text"
            name="tenancyName"
            value={form.tenancyName}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={TENANCY_NAME_MAX_LENGTH}
            placeholder="acme"
            disabled={loading}
            className={fieldClass('tenancyName')}
            autoComplete="off"
          />
          {touched.tenancyName && errors.tenancyName ? (
            <p className="mt-1 text-xs text-red-500">{errors.tenancyName}</p>
          ) : (
            previewUrl && <p className="mt-1 text-xs text-gray-400 break-all">{previewUrl}</p>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">First name</label>
            <input
              type="text"
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={NAME_MAX_LENGTH}
              disabled={loading}
              className={fieldClass('firstName')}
            />
            {touched.firstName && errors.firstName && (
              <p className="mt-1 text-xs text-red-500">{errors.firstName}</p>
            )}
          </div>
          <div>
            <label className="block mb-1 text-sm font-medium text-gray-700">Last name</label>
            <input
              type="text"
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              maxLength={NAME_MAX_LENGTH}
              disabled={loading}
              className={fieldClass('lastName')}
            />
            {touched.lastName && errors.lastName && (
              <p className="mt-1 text-xs text-red-500">{errors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block mb-1 text-sm font-medium text-gray-700">
            Administrator email
          </label>
          <input
            type="email"
            name="emailAddress"
            value={form.emailAddress}
            onChange={handleChange}
            onBlur={handleBlur}
            maxLength={EMAIL_MAX_LENGTH}
            placeholder="admin@company.com"
            disabled={loading}
            className={fieldClass('emailAddress')}
          />
          {touched.emailAddress && errors.emailAddress && (
            <p className="mt-1 text-xs text-red-500">{errors.emailAddress}</p>
          )}
        </div>

        {apiError && (
          <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {apiError}
          </div>
        )}

        {result && (
          <div className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-800">
            <p className="font-medium">Tenant "{result.tenancyName}" created successfully.</p>
            <p className="mt-1">
              Welcome email sent to <span className="font-medium">{result.email}</span>.
            </p>
            <p className="mt-1 break-all">
              URL:{' '}
              <a
                href={result.url}
                target="_blank"
                rel="noreferrer"
                className="text-[#62accb] hover:underline"
              >
                {result.url}
              </a>
            </p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={loading}
            className="inline-flex items-center gap-2 px-5 py-2 bg-[#62accb] hover:bg-[#4a9bd1] text-white text-sm rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading && <Loader2 className="w-4 h-4 animate-spin" />}
            {loading ? 'Creating tenant...' : 'Create tenant'}
          </button>
        </div>
        {loading && (
          <p className="text-xs text-gray-400 text-right">
            This can take a minute: the tenant database is being created and migrated.
          </p>
        )}
      </form>
    </div>
  );
};
